/* istanbul ignore file */

import * as Sentry from '@sentry/react'
import axios from 'axios'

import { helpers, httpStatuses } from '@reveni-io/reveni-ui'

import { decipherJwt, getLocalStorageForSentry } from './common/helpers/helpers'
import generalServices from './general/services/services'
import { i18nInstance } from '.'

const sendErrorToSentry = err => {
  if (import.meta.env.VITE_SENTRY_ENVIRONMENT && err.response) {
    let url = err.response?.request?.responseURL?.replaceAll(/(\w){32}/g, ':uuid')

    const newError = new Error(url)

    newError.name = `Error ${err.response?.status}: ${url}`
    newError.stack = `Error ${err.response?.status}: ${url}`

    Sentry.captureException(newError, {
      extra: {
        body: err.request?.__sentry_xhr__,
        localStorage: getLocalStorageForSentry(),
        response: err?.response?.data ? JSON.stringify(err.response.data) : null,
        version: VERSION,
      },
    })
  }
}

const redirectToLogin = () => {
  //avoid redirect in localhost because token exp is mocked
  if (window.location.hostname !== 'localhost') {
    localStorage.clear()
    window.location.href = '/login'
  }
}

let refreshing = false

const checkToken = async (token, refreshToken) => {
  const jwt = decipherJwt(token)

  const expiresAt = new Date(jwt.exp * 1000)
  const now = new Date()

  if (expiresAt - now < 0) {
    redirectToLogin()
  } else {
    const refreshThreshold = 300014764 // 3 days
    if (expiresAt - now < refreshThreshold) {
      refreshing = true
      try {
        const response = await generalServices.refreshToken(refreshToken)

        localStorage.jwt = response.data.access
        localStorage.refresh = response.data.refresh

        refreshing = false
      } catch {
        refreshing = false
        redirectToLogin()
      }
    }
  }
}

axios.interceptors.request.use(config => {
  config.headers['Accept-Language'] = i18nInstance.language

  if (localStorage.jwt && localStorage.refresh && refreshing === false) {
    checkToken(localStorage.jwt, localStorage.refresh)
  }

  if (
    ![
      `${import.meta.env.VITE_HOST}/token`,
      `${import.meta.env.VITE_HOST}/token/refresh`,
      `${import.meta.env.VITE_HOST}/reset-password`,
      `${import.meta.env.VITE_HOST}/token/social`,
    ].includes(config.url)
  ) {
    config.headers['Authorization'] = `Bearer ${localStorage.jwt}`
  }
  return config
})

axios.interceptors.response.use(
  response => {
    return response
  },
  error => {
    error.errorMessage = helpers.getErrorMessage(error)
    sendErrorToSentry(error)
    if (error?.response?.status === httpStatuses.unauthorized) {
      localStorage.clear()
    }
    return Promise.reject(error)
  }
)
